import { useSettings } from '@hooks/useSettings';
import type {
    Decision,
    EventTags,
    ExperimentOptions,
    ExperimentsClient,
} from '@ingka-group-digital/window-ikea-typings/src/experimentum/exptSDK';
import { shouldEnableAllExperiments } from 'lib/global';
import React, {
    createContext,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { OptimizelyContextType } from './types';

type OptimizelyProviderProps = {
    children: React.ReactNode;
};

export const OptimizelyContext = createContext<OptimizelyContextType>({
    activateExperiments: () => undefined,
    decide: () => null,
    trackEvent: () => undefined,
    isExperimentOn: () => false,
    getVariationKey: () => '',
});

export const OptimizelyProvider: React.FC<OptimizelyProviderProps> = ({
    children,
}) => {
    const { optimizelyKey } = useSettings();
    const [wrapper, setWrapper] = useState<ExperimentsClient | undefined>(
        undefined
    );
    const decisionCache = useRef<Record<string, Decision>>({});

    useEffect(() => {
        const init = async () => {
            if (wrapper || !optimizelyKey) {
                return;
            }
            try {
                if (!(window?.ikea?.cookieConsent?.hasConsent(3) ?? true)) {
                    return;
                }
                if (!window.ikea?.exptSDK?.init) {
                    console.warn('missing window.ikea.exptSDK.init');
                    return;
                }

                const options: ExperimentOptions = {
                    contentSquare: {
                        enabled: true,
                        category: 'favourites',
                    },
                    ikeaAnalytics: {
                        enabled: true,
                        team: 'web-cart',
                        category: 'favourites',
                    },
                };
                const { client: _wrapper } = await window.ikea.exptSDK.init(
                    optimizelyKey,
                    options
                );
                setWrapper(_wrapper);
            } catch {
                // Fail silently.
            }
        };

        init();
        window.ikea?.pubsub?.subscribe('ikeaCookieConsent/changed', init);
        return () => {
            window.ikea?.pubsub?.unsubscribe('ikeaCookieConsent/changed', init);
        };
    }, [optimizelyKey, wrapper]);

    const decide = useCallback(
        (decisionKey: string) => {
            const cached = decisionCache.current[decisionKey];

            if (cached) {
                return cached;
            }
            const result = wrapper?.decide?.(decisionKey);
            if (result?.decision) {
                decisionCache.current[decisionKey] = result?.decision;
            }
            return result?.decision ?? null;
        },
        [wrapper]
    );

    const ctx = useMemo<OptimizelyContextType>(
        () => ({
            activateExperiments: () => undefined,
            trackEvent: (
                eventName: string,
                eventAttributes?: Record<string, string | number | null>,
                eventTags?: EventTags
            ) => {
                try {
                    wrapper?.track?.(eventName, {
                        eventAttributes,
                        eventTags,
                    });
                } catch {
                    // Fail silently.
                }
            },
            decide: (decisionKey: string) => {
                try {
                    return decide(decisionKey);
                } catch {
                    return null;
                }
            },
            isExperimentOn: (experimentKey: string): boolean => {
                try {
                    return (
                        shouldEnableAllExperiments ||
                        decide(experimentKey)?.variationKey === 'on'
                    );
                } catch {
                    return false;
                }
            },
            getVariationKey: (experimentKey: string): string => {
                try {
                    return decide(experimentKey)?.variationKey || '';
                } catch {
                    return '';
                }
            },
        }),
        [decide, wrapper]
    );

    return (
        <OptimizelyContext.Provider value={ctx}>
            {children}
        </OptimizelyContext.Provider>
    );
};
